import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatAngularModule } from '../../../modules/mat-angular.module';
import { NgxChartsModule } from '@swimlane/ngx-charts';

// Reusable components
import { BarChartComponent } from '../../../charts/bar-chart/bar-chart.component';
import { PieGridComponent } from '../../../charts/pie-grid/pie-grid.component';
import { PolarChartComponent } from '../../../charts/polar-chart/polar-chart.component';
import { TableComponent, TableColumn } from '../../table/table.component';
import { ScoreCardsComponent } from '../../score-cards/score-cards.component';


@Component({
  selector: 'app-common-consumer-insights',
  imports: [
    CommonModule,
    MatAngularModule,
    NgxChartsModule,
    BarChartComponent,
    PieGridComponent,
    PolarChartComponent,
    TableComponent,
    ScoreCardsComponent,

  ],
  templateUrl: './common-consumer-insights.component.html',
  styleUrl: './common-consumer-insights.component.scss'
})
export class CommonConsumerInsightsComponent implements OnInit, OnChanges {
  @Input() data: any = null;
  @Input() universities: string[] = [];
  @Input() title: string = 'Consumer Insights';

  // Toggle: show legacy three tables or the new combined table (default: combined only)
  showDetailedSections: boolean = false;

  // Chart data for different visualizations
  enrollmentChartData: any[] = [];
  satisfactionChartData: any[] = [];
  careerOutcomesChartData: any[] = [];
  decisionFactorsChartData: any[] = [];
  satisfactionRadarData: any[] = [];

  // Table data
  enrollmentTableData: any[] = [];
  satisfactionTableData: any[] = [];
  careerTableData: any[] = [];
  demographicsTableData: any[] = [];
  combinedTableData: any[] = [];

  // Table configurations
  enrollmentTableColumns: TableColumn[] = [
    { key: 'university', label: 'University', type: 'text' },
    { key: 'total_applications', label: 'Total Applications', type: 'number' },
    { key: 'admitted', label: 'Admitted', type: 'number' },
    { key: 'enrolled', label: 'Enrolled', type: 'number' },
    { key: 'yield_rate', label: 'Yield Rate (%)', type: 'percentage' }
  ];

  satisfactionTableColumns: TableColumn[] = [
    { key: 'university', label: 'University', type: 'text' },
    { key: 'overall_satisfaction', label: 'Overall Satisfaction', type: 'percentage' },
    { key: 'teaching_quality', label: 'Teaching Quality', type: 'percentage' },
    { key: 'facilities_rating', label: 'Facilities Rating', type: 'percentage' },
    { key: 'campus_life', label: 'Campus Life', type: 'percentage' },
    { key: 'retention_rate', label: 'Retention Rate (%)', type: 'percentage' }
  ];

  careerTableColumns: TableColumn[] = [
    { key: 'university', label: 'University', type: 'text' },
    { key: 'placement_rate', label: 'Placement Rate (%)', type: 'percentage' },
    { key: 'average_salary', label: 'Average Salary (£)', type: 'currency' },
    { key: 'top_industries', label: 'Top Industries', type: 'text' },
    { key: 'top_companies', label: 'Top Companies', type: 'text' }
  ];

  demographicsTableColumns: TableColumn[] = [
    { key: 'university', label: 'University', type: 'text' },
    { key: 'average_age', label: 'Average Age', type: 'number' },
    { key: 'work_experience', label: 'Work Experience (Years)', type: 'number' },
    { key: 'international_students', label: 'International Students (%)', type: 'percentage' },
    { key: 'study_mode', label: 'Study Mode Preference', type: 'text' }
  ];

  combinedTableColumns: TableColumn[] = [
    { key: 'university', label: 'University', type: 'text' },
    { key: 'total_applications', label: 'Total Applications', type: 'number' },
    { key: 'admitted', label: 'Admitted', type: 'number' },
    { key: 'enrolled', label: 'Enrolled', type: 'number' },
    { key: 'yield_rate', label: 'Yield Rate (%)', type: 'percentage' },
    { key: 'overall_satisfaction', label: 'Overall Satisfaction', type: 'percentage' },
    { key: 'teaching_quality', label: 'Teaching Quality', type: 'percentage' },
    { key: 'facilities_rating', label: 'Facilities Rating', type: 'percentage' },
    { key: 'campus_life', label: 'Campus Life', type: 'percentage' },
    { key: 'retention_rate', label: 'Retention Rate (%)', type: 'percentage' },
    { key: 'placement_rate', label: 'Placement Rate (%)', type: 'percentage' },
    { key: 'average_salary', label: 'Average Salary (£)', type: 'currency' },
  ];

  // Course Finder integration data
  courseFinderData: any = null;
  searchAnalytics: any = null;
  studentPreferences: any = null;
  applicationFunnel: any = null;

  // Comparative insights
  comparativeInsights: any = null;

  // Evidence sources and recommendations
  evidenceSources: any[] = [];
  recommendations: any[] = [];

  ngOnInit(): void {
    this.processData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.processData();
    }
  }

  private processData(): void {
    if (!this.data) return;

    // Handle both direct data and transformed data from service
    const responseData = this.data.consumer_insights_data || this.data;

    // Extract evidence sources and recommendations
    this.evidenceSources = responseData.evidence_sources || [];
    this.recommendations = responseData.recommendations || [];
    this.comparativeInsights = responseData.comparative_insights || null;
    this.courseFinderData = responseData.course_finder_integration || null;

    // Process universities data from the new API structure
    if (responseData.data && Array.isArray(responseData.data)) {
      this.processUniversitiesData(responseData.data);
    }

    // Process table data from the new API structure
    if (responseData.table && Array.isArray(responseData.table)) {
      this.processTableData(responseData.table);
    }

    // Process chart data from the new API structure
    if (responseData.chart && responseData.chart.series) {
      this.processChartData(responseData.chart.series);
    }

    // Process course finder data
    if (this.courseFinderData) {
      this.processCourseFinderData();
    }
  }

  private processUniversitiesData(universities: any[]): void {
    // Create satisfaction radar chart data from the series
    this.satisfactionRadarData = universities.map(uni => ({
      name: uni.name,
      series: uni.series || []
    }));

    // Create decision factors pie chart data from the first university's series
    if (universities.length > 0 && universities[0].series) {
      this.decisionFactorsChartData = universities[0].series.map((item: any) => ({
        name: item.name,
        value: item.value
      }));
    }
  }

  private processTableData(tableData: any[]): void {
    // Process enrollment data from table
    this.enrollmentTableData = tableData.map(item => ({
      university: item.university,
      total_applications: item.enrollment_rate || 0,
      admitted: item.enrollment_rate || 0,
      enrolled: item.enrollment_rate || 0,
      yield_rate: item.enrollment_rate || 0
    }));

    // Process satisfaction data from table
    this.satisfactionTableData = tableData.map(item => ({
      university: item.university,
      overall_satisfaction: item.student_satisfaction || 0,
      teaching_quality: item.student_satisfaction || 0,
      facilities_rating: item.student_satisfaction || 0,
      campus_life: item.student_satisfaction || 0,
      retention_rate: item.retention_rate || 0
    }));

    // Process career outcomes data from table
    this.careerTableData = tableData.map(item => ({
      university: item.university,
      placement_rate: item.career_placement || 0,
      average_salary: item.average_salary || 0,
      top_industries: 'N/A',
      top_companies: 'N/A'
    }));

    // NEW: Combined table data (single table view)
    this.combinedTableData = tableData.map(item => ({
      university: item.university,
      total_applications: item.enrollment_rate || 0,
      admitted: item.enrollment_rate || 0,
      enrolled: item.enrollment_rate || 0,
      yield_rate: item.enrollment_rate || 0,
      overall_satisfaction: item.student_satisfaction || 0,
      teaching_quality: item.student_satisfaction || 0,
      facilities_rating: item.student_satisfaction || 0,
      campus_life: item.student_satisfaction || 0,
      retention_rate: item.retention_rate || 0,
      placement_rate: item.career_placement || 0,
      average_salary: item.average_salary || 0,
    }));

    // Ensure combined table has rows; if so, hide legacy tables by default
    this.showDetailedSections = false;
  }

  private processChartData(chartData: any[]): void {
    // Process enrollment chart data
    this.enrollmentChartData = chartData.map(uni => ({
      name: uni.name,
      series: uni.series?.filter((s: any) => s.name === 'Enrollment Rate') || []
    })).filter(uni => uni.series.length > 0);

    // Process satisfaction chart data
    this.satisfactionChartData = chartData.map(uni => ({
      name: uni.name,
      series: uni.series?.filter((s: any) => s.name === 'Student Satisfaction') || []
    })).filter(uni => uni.series.length > 0);

    // Process career outcomes chart data
    this.careerOutcomesChartData = chartData.map(uni => ({
      name: uni.name,
      series: uni.series?.filter((s: any) => s.name === 'Career Outcomes') || []
    })).filter(uni => uni.series.length > 0);
  }

  private processCourseFinderData(): void {
    this.searchAnalytics = this.courseFinderData.search_analytics || null;
    this.studentPreferences = this.courseFinderData.student_preferences || null;
    this.applicationFunnel = this.courseFinderData.application_funnel || null;
  }

  private getStudyModePreference(preference: any): string {
    if (!preference) return 'N/A';
    const onCampus = preference.on_campus || 0;
    const online = preference.online || 0;
    return onCampus > online ? 'On-Campus' : 'Online';
  }

  private formatDecisionFactorName(key: string): string {
    return key.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  // Helper methods for template
  getEnrollmentTableColumns(): string[] {
    return this.enrollmentTableColumns.map(col => col.key);
  }

  getSatisfactionTableColumns(): string[] {
    return this.satisfactionTableColumns.map(col => col.key);
  }

  getCareerTableColumns(): string[] {
    return this.careerTableColumns.map(col => col.key);
  }

  getDemographicsTableColumns(): string[] {
    return this.demographicsTableColumns.map(col => col.key);
  }

  getCombinedTableColumns(): string[] {
    return this.combinedTableColumns.map(col => col.key);
  }

  hasEnrollmentData(): boolean {
    return this.enrollmentTableData.length > 0;
  }

  hasSatisfactionData(): boolean {
    return this.satisfactionTableData.length > 0;
  }

  hasCareerData(): boolean {
    return this.careerTableData.length > 0;
  }

  hasDemographicsData(): boolean {
    return this.demographicsTableData.length > 0;
  }

  hasCombinedData(): boolean {
    return this.combinedTableData.length > 0;
  }

  hasCourseFinderData(): boolean {
    return this.courseFinderData !== null;
  }

  hasComparativeInsights(): boolean {
    return this.comparativeInsights !== null;
  }
}
